import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';

export const RELOAD_FLAG = 'yak:lastAutoReload';
// If a reload happened within this window we assume the error is persistent (the
// reload did not clear it) and stop auto-reloading, showing the manual UI instead.
export const RELOAD_GUARD_MS = 15000;

// Fallback rendered by the top-level Bugsnag ErrorBoundary. Without it, a caught
// render error leaves a permanent blank screen that only a manual refresh clears.
// This auto-performs that refresh once (guarding against reload loops), and on a
// persistent error surfaces the underlying message so it can be diagnosed from a
// console log / screenshot. Bugsnag passes { error, info } as props.
export default class AppErrorFallback extends PureComponent {
  static propTypes = {
    error: PropTypes.instanceOf(Error),
    info: PropTypes.shape({componentStack: PropTypes.string}),
  };

  static defaultProps = {
    error: null,
    info: null,
  };

  componentDidMount() {
    const {error, info} = this.props;

    // Always log the underlying error so a persistent crash is diagnosable even
    // when the on-screen stack is minified. The error *message* (e.g. "Cannot
    // read properties of undefined (reading 'silver')") names the property and is
    // not minified.
    try {
      // eslint-disable-next-line no-console
      console.error(
        '[AppError]',
        error && (error.stack || error.message),
        info && info.componentStack,
      );
    } catch (e) {
      // ignore
    }

    let lastReload = 0;
    try {
      lastReload = Number(window.sessionStorage.getItem(RELOAD_FLAG)) || 0;
    } catch (e) {
      lastReload = 0;
    }

    if (Date.now() - lastReload > RELOAD_GUARD_MS) {
      try {
        window.sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
      } catch (e) {
        // sessionStorage may be unavailable (private mode); reload anyway.
      }
      window.location.reload();
    }
  }

  handleReload = () => {
    try {
      window.sessionStorage.removeItem(RELOAD_FLAG);
    } catch (e) {
      // ignore
    }
    window.location.reload();
  };

  render() {
    const {error, info} = this.props;
    const message = error ? (error.message || String(error)) : '';
    const componentStack = info && info.componentStack
      ? info.componentStack.split('\n').filter(Boolean).slice(0, 6).join('\n')
      : '';

    return (
      <div className="app-error" style={{padding: '2rem', textAlign: 'center'}}>
        <h1>Something went wrong</h1>
        <p>We hit a snag loading this screen. Reloading usually fixes it.</p>
        <button type="button" onClick={this.handleReload}>Reload</button>
        {' '}
        <a href="/lobby">Back to lobby</a>
        {(message || componentStack) && (
          <pre
            style={{
              marginTop: '1.5rem',
              textAlign: 'left',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontSize: '11px',
              color: '#999',
            }}
          >
            {message}
            {componentStack ? `\n${componentStack}` : ''}
          </pre>
        )}
      </div>
    );
  }
}
